import { SkiPlan, SiteElement, Path, PathPoint } from '../types';
import jsPDF from 'jspdf';
import * as htmlToImage from 'html-to-image';
import { getElementColorForPDF, hexToRgb } from './elementConfig';

export async function generatePreview(plan: SkiPlan): Promise<string> {
  // Generate the composite canvas for preview
  return await createCompositeCanvas(plan);
}

export async function exportToPDF(plan: SkiPlan, includeMetadata: boolean = true, includeLegend: boolean = true) {
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;
  let yPos = margin;

  if (includeMetadata) {
    // Title - Centered
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    const title = plan.name || 'Course';
    const titleWidth = pdf.getTextWidth(title);
    pdf.text(title, (pageWidth - titleWidth) / 2, margin + 7);

    // Metadata in two columns to save vertical space
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    yPos = margin + 14;

    const col1X = margin;
    const col2X = margin + 65;
    const col3X = margin + 130;
    let col1Y = yPos;
    let col2Y = yPos;
    let col3Y = yPos;

    // Column 1
    if (plan.location) {
      pdf.text(`Lieu: ${plan.location}`, col1X, col1Y);
      col1Y += 4.5;
    }

    if (plan.date) {
      const date = new Date(plan.date);
      pdf.text(`Date: ${date.toLocaleDateString('fr-FR')}`, col1X, col1Y);
      col1Y += 4.5;
    }

    // Column 2
    if (plan.raceType) {
      pdf.text(`Type: ${plan.raceType}`, col2X, col2Y);
      col2Y += 4.5;
    }

    if (plan.technique) {
      const techniques: { [key: string]: string } = {
        classique: 'Classique',
        skating: 'Skating',
        libre: 'Libre'
      };
      pdf.text(`Technique: ${techniques[plan.technique] || plan.technique}`, col2X, col2Y);
      col2Y += 4.5;
    }

    // Column 3
    if (plan.totalDistance) {
      pdf.text(`Distance: ${plan.totalDistance}m`, col3X, col3Y);
      col3Y += 4.5;
    }

    if (plan.numLaps) {
      pdf.text(`Tours: ${plan.numLaps}`, col3X, col3Y);
      col3Y += 4.5;
    }

    // Set yPos to the maximum Y position used + small margin
    yPos = Math.max(col1Y, col2Y, col3Y) + 3;
  }

  // Add site image with overlays
  let imgX = margin, imgY = yPos, imgWidth = 0, imgHeight = 0;
  
  if (plan.siteImage) {
    // Legend is now overlaid, so no need to reserve space for it
    const availableWidth = pageWidth - 2 * margin;
    
    // Calculate available height considering the metadata space used
    const startY = includeMetadata ? yPos : margin;
    const availableHeight = pageHeight - startY - margin;
    
    try {
      // Create a composite image with all overlays
      const compositeImage = await createCompositeCanvas(plan);
      
      // Load the image to get its actual dimensions
      const img = await loadImage(compositeImage);
      
      // Calculate dimensions respecting actual aspect ratio (object-contain behavior)
      const imageRatio = img.width / img.height;
      const availableRatio = availableWidth / availableHeight;
      
      if (imageRatio > availableRatio) {
        // Image is wider - fit to width
        imgWidth = availableWidth;
        imgHeight = imgWidth / imageRatio;
      } else {
        // Image is taller - fit to height
        imgHeight = availableHeight;
        imgWidth = imgHeight * imageRatio;
      }
      
      // Center the image in the available space
      imgX = margin + (availableWidth - imgWidth) / 2;
      imgY = startY + (availableHeight - imgHeight) / 2;
      
      pdf.addImage(compositeImage, 'PNG', imgX, imgY, imgWidth, imgHeight);
    } catch (error) {
      console.error('Error creating composite image:', error);
      // Fallback to just the base image
      try {
        const img = await loadImage(plan.siteImage);
        const imageRatio = img.width / img.height;
        const availableRatio = availableWidth / availableHeight;
        
        if (imageRatio > availableRatio) {
          imgWidth = availableWidth;
          imgHeight = imgWidth / imageRatio;
        } else {
          imgHeight = availableHeight;
          imgWidth = imgHeight * imageRatio;
        }
        
        imgX = margin + (availableWidth - imgWidth) / 2;
        imgY = startY + (availableHeight - imgHeight) / 2;
        
        pdf.addImage(plan.siteImage, 'JPEG', imgX, imgY, imgWidth, imgHeight);
      } catch (e) {
        console.error('Error adding fallback image:', e);
      }
    }
  }

  // Add legend overlaid at bottom right (only if enabled)
  if (includeLegend && plan.siteImage) {
    // Position legend at bottom right corner of the image with some padding
    const legendPadding = 5;
    const legendWidth = 52;
    const legendX = imgX + imgWidth - legendWidth - legendPadding;
    let legendY = imgY + imgHeight - legendPadding;
    
    // Calculate legend height first to draw background
    let tempLegendY = legendY;
    const legendStartY = tempLegendY;
    
    // Calculate approximate legend height
    let legendContentHeight = 7; // Title
    if (plan.elements.length > 0) {
      legendContentHeight += 5 + (getUniqueElementTypes(plan.elements).length * 4);
    }
    if (plan.paths.length > 0) {
      legendContentHeight += 8 + (getUniquePathTypes(plan.paths).length * 4);
    }
    
    const legendBoxY = legendY - legendContentHeight;
    const legendBoxHeight = legendContentHeight + 3;
    
    // Draw background box with border (semi-transparent white)
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(legendX - 2, legendBoxY - 2, legendWidth + 4, legendBoxHeight, 2, 2, 'F');
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.roundedRect(legendX - 2, legendBoxY - 2, legendWidth + 4, legendBoxHeight, 2, 2, 'S');
    
    // Reset position for actual legend content
    legendY = legendBoxY + 1;

    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Légende', legendX, legendY);
    legendY += 6;

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');

    // Elements legend
    if (plan.elements.length > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Éléments:', legendX, legendY);
      legendY += 4;
      pdf.setFont('helvetica', 'normal');

      const elementTypes = getUniqueElementTypes(plan.elements);
      elementTypes.forEach((element) => {
        const color = getElementColorForPDF(element.type, element.color);
        
        // Draw color circle
        pdf.setFillColor(color.r, color.g, color.b);
        pdf.circle(legendX + 2, legendY - 1, 1.2, 'F');
        
        // Element label (truncate if too long)
        const labelText = element.label || 'Sans nom';
        const truncated = labelText.length > 18 ? labelText.substring(0, 16) + '...' : labelText;
        pdf.setTextColor(0, 0, 0);
        pdf.text(truncated, legendX + 5, legendY);
        legendY += 4;
      });

      legendY += 2;
    }

    // Paths legend
    if (plan.paths.length > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Tracés:', legendX, legendY);
      legendY += 4;
      pdf.setFont('helvetica', 'normal');

      const pathTypes = getUniquePathTypes(plan.paths);
      pathTypes.forEach((path) => {
        const color = getPathColorForPDF(path.type, path.color);
        const label = getPathTypeLabel(path.type);
        
        // Draw color line
        pdf.setDrawColor(color.r, color.g, color.b);
        pdf.setLineWidth(0.5);
        pdf.line(legendX, legendY - 0.5, legendX + 4, legendY - 0.5);
        pdf.setDrawColor(0, 0, 0);
        
        // Path label (truncate if too long)
        if (label && typeof label === 'string') {
          const truncated = label.length > 18 ? label.substring(0, 16) + '...' : label;
          pdf.setTextColor(0, 0, 0);
          pdf.text(truncated, legendX + 5, legendY);
        }
        legendY += 4;
      });
    }
  }

  // Save PDF
  const fileName = `${plan.name || 'plan-ski'}-${new Date().getTime()}.pdf`;
  pdf.save(fileName);
}

function getPathColorForPDF(type: Path['type'], customColor?: string): { r: number; g: number; b: number } {
  if (customColor) {
    return hexToRgb(customColor);
  }

  const colors: { [key: string]: string } = {
    course: '#3b82f6', // Bleu
    net: '#ef4444', // Rouge
    barrier: '#8b4513', // Marron (SaddleBrown)
    'v-board': '#ef4444', // Rouge
    sapinettes: '#ef4444', // Rouge
    custom: '#8b5cf6' // Violet
  };

  return hexToRgb(colors[type] || '#3b82f6');
}

function getUniqueElementTypes(elements: SiteElement[]): SiteElement[] {
  const seen = new Set<string>();
  const unique: SiteElement[] = [];

  elements.forEach((element) => {
    const key = element.type === 'custom' ? `custom-${element.label}` : element.type;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(element);
    }
  });

  return unique;
}

function getUniquePathTypes(paths: Path[]): Path[] {
  const seen = new Set<string>();
  const unique: Path[] = [];

  paths.forEach((path) => {
    const key = path.label ? `${path.type}-${path.label}` : path.type;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(path);
    }
  });

  return unique;
}

function getPathTypeLabel(type: Path['type']): string {
  const labels: Record<Path['type'], string> = {
    course: 'Tracé de course',
    net: 'Filet',
    barrier: 'Barrière',
    'v-board': 'V-Board',
    sapinettes: 'Sapinettes'
  };
  return labels[type] || type;
}

async function createCompositeCanvas(plan: SkiPlan): Promise<string> {     
     // Try to capture the actual canvas from the editor for pixel-perfect export
     const root = document.querySelector('[data-canvas-inner]') as HTMLElement | null;
     const layer = document.querySelector('[data-image-layer]') as HTMLElement | null;

  if (root && layer) {
    try {
      // Suppress console errors from browser extensions
      const originalConsoleError = console.error;
      const originalConsoleWarn = console.warn;
      console.error = (...args: any[]) => {
        const message = args[0]?.toString() || '';
        // Ignore CORS and extension-related errors
        if (message.includes('cross-origin') || 
            message.includes('moz-extension') || 
            message.includes('chrome-extension') ||
            message.includes('CSSStyleSheet') ||
            message.includes('SecurityError')) {
          return;
        }
        originalConsoleError.apply(console, args);
      };
      console.warn = (...args: any[]) => {
        const message = args[0]?.toString() || '';
        if (message.includes('cross-origin') || 
            message.includes('moz-extension') || 
            message.includes('chrome-extension')) {
          return;
        }
        originalConsoleWarn.apply(console, args);
      };

      // Use htmlToImage to capture the actual rendered canvas with all lucide icons
      const pixelRatio = 2;
      const full = await htmlToImage.toPng(root, {
        backgroundColor: '#f3f4f6',
        pixelRatio, // High quality capture
        preferredFontFormat: 'woff2',
        skipFonts: true, // Skip font processing to avoid extension issues
        skipAutoScale: false,
        cacheBust: false, // Don't add timestamps to avoid CORS issues
        // Ignore overlays and interactive elements
        filter: (element) => {
          // Filter out browser extension elements
          const tagName = element.tagName?.toLowerCase() || '';
          const elementId = (element as HTMLElement).id || '';
          const elementClass = (element as HTMLElement).className || '';
          
          // Block all extension-related elements
          if (tagName === 'link' || tagName === 'style') {
            const href = element.getAttribute('href') || '';
            const textContent = element.textContent || '';
            if (href.includes('extension://') || 
                textContent.includes('extension://') ||
                href.includes('moz-extension://') ||
                textContent.includes('moz-extension://')) {
              return false;
            }
          }
          
          // Block extension-injected elements
          if (elementId.includes('extension') || 
              elementClass.toString().includes('extension') ||
              elementId.includes('grammarly') ||
              elementClass.toString().includes('grammarly')) {
            return false;
          }
          
          // Include the image
          if (tagName === 'img') {
            return true;
          }
          
          // Check if element or its parent is a marker button
          let currentElement = element as HTMLElement;
          while (currentElement) {
            if (currentElement.hasAttribute && currentElement.hasAttribute('data-element-marker')) {
              return true; // Include all elements inside markers
            }
            currentElement = currentElement.parentElement as HTMLElement;
          }
          
          // Allow SVG elements (for paths and Lucide icons)
          if (tagName === 'svg') {
            return true;
          }
          
          // Allow all SVG child elements (paths, circles, etc.)
          if (element.parentElement && element.parentElement.tagName?.toLowerCase() === 'svg') {
            return true;
          }
          
          // Exclude tooltips (they have 'opacity-0' class)
          const classList = (element as HTMLElement).classList;
          if (classList && (classList.contains('opacity-0') || classList.contains('pointer-events-none'))) {
            // But only if not inside a marker
            const parentMarker = (element as HTMLElement).closest?.('[data-element-marker]');
            if (!parentMarker) {
              return false;
            }
          }
          
          // Exclude control buttons and ignored elements
          if (tagName === 'button') {
            return (element as HTMLElement).hasAttribute('data-element-marker');
            }
            return !element.hasAttribute('data-ignore-export') && tagName !== 'button';
           },
      });
      
      const rootRect = root.getBoundingClientRect();
      const layerRect = layer.getBoundingClientRect();

      const cropX = Math.round((layerRect.left - rootRect.left) * pixelRatio);
      const cropY = Math.round((layerRect.top - rootRect.top) * pixelRatio);
      const cropW = Math.round(layerRect.width * pixelRatio);
      const cropH = Math.round(layerRect.height * pixelRatio);

      return await cropPngDataUrl(full, cropX, cropY, cropW, cropH);

      // Restore console methods
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      
      return canvas;
    } catch (error) {
      console.warn('htmlToImage failed, falling back to manual rendering:', error);
      // Fall through to manual rendering
    }
  }
  
  // Fallback: manual rendering if htmlToImage fails or canvas not found
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    // Load the base image first to get its dimensions
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      // Use high resolution canvas matching the editor
      canvas.width = 1920;
      canvas.height = 1080;

      // Draw background
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Calculate dimensions to match object-contain behavior (like the editor)
      // object-contain: the image is scaled to maintain aspect ratio while fitting inside the container
      const imgRatio = img.width / img.height;
      const canvasRatio = canvas.width / canvas.height;
      
      let drawWidth, drawHeight, drawX, drawY;
      
      if (imgRatio > canvasRatio) {
        // Image is wider than canvas - fit to width
        drawWidth = canvas.width;
        drawHeight = drawWidth / imgRatio;
        drawX = 0;
        drawY = (canvas.height - drawHeight) / 2;
      } else {
        // Image is taller than canvas - fit to height
        drawHeight = canvas.height;
        drawWidth = drawHeight * imgRatio;
        drawX = (canvas.width - drawWidth) / 2;
        drawY = 0;
      }

      // Draw the site image with object-contain behavior (matching the editor)
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

      // Draw paths
      plan.paths.forEach((path) => {
        drawPathOnCanvas(ctx, path, canvas.width, canvas.height);
      });

      // Draw elements
      plan.elements.forEach((element) => {
        drawElementOnCanvas(ctx, element, canvas.width, canvas.height);
      });

      // Convert to data URL
      resolve(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = plan.siteImage!;
  });
}

function drawPathOnCanvas(ctx: CanvasRenderingContext2D, path: Path, width: number, height: number) {
  if (path.points.length < 2) return;

  const color = path.color || getDefaultPathColor(path.type);
  const scaleFactor = width / 1920; // Reference width for scaling
  const strokeWidth = (path.strokeWidth || 0.5) * (width / 100);
  const defaultOpacity = path.type === 'course' ? 0.5 : 1;
  const opacity = path.opacity ?? defaultOpacity;

  ctx.save();
  ctx.globalAlpha = opacity;
  ctx.strokeStyle = color;
  ctx.lineWidth = strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // Handle dash array
  if (path.dashArray && path.dashArray !== 'none') {
    const dashArray = path.dashArray.split(',').map(v => parseFloat(v.trim()) * (width / 100));
    ctx.setLineDash(dashArray);
  }

  // Draw the path
  ctx.beginPath();
  const firstPoint = path.points[0];
  ctx.moveTo((firstPoint.x / 100) * width, (firstPoint.y / 100) * height);

  if (path.smooth) {
    // Draw smooth curves using Catmull-Rom splines
    const tension = path.tension || 0.5;
    
    for (let i = 0; i < path.points.length - 1; i++) {
      const p0 = i > 0 ? path.points[i - 1] : path.points[i];
      const p1 = path.points[i];
      const p2 = path.points[i + 1];
      const p3 = i + 2 < path.points.length ? path.points[i + 2] : p2;

      const alpha = tension;
      
      // Calculate control points for cubic Bézier curve
      const cp1x = (p1.x + (p2.x - p0.x) / 6 * alpha) / 100 * width;
      const cp1y = (p1.y + (p2.y - p0.y) / 6 * alpha) / 100 * height;
      const cp2x = (p2.x - (p3.x - p1.x) / 6 * alpha) / 100 * width;
      const cp2y = (p2.y - (p3.y - p1.y) / 6 * alpha) / 100 * height;

      ctx.bezierCurveTo(
        cp1x, cp1y,
        cp2x, cp2y,
        (p2.x / 100) * width, (p2.y / 100) * height
      );
    }
  } else {
    // Draw straight lines
    for (let i = 1; i < path.points.length; i++) {
      const point = path.points[i];
      ctx.lineTo((point.x / 100) * width, (point.y / 100) * height);
    }
  }

  ctx.stroke();

  // Draw arrows for course paths with uniform distribution
  if (path.type === 'course') {
    const arrowOpacity = path.arrowOpacity ?? 1;
    ctx.globalAlpha = arrowOpacity;
    ctx.setLineDash([]);

    const arrowPositions = getArrowPositions(path, width, height);
    const arrowSize = ((path.arrowSize || 100) / 100) * (width / 100);

    arrowPositions.forEach(({ x, y, angle }) => {
      drawArrow(ctx, x, y, angle, arrowSize, color, path.arrowStyle || 'triangle');
    });
  }

  ctx.restore();
}

// Calculate arrow positions uniformly distributed along path
function getArrowPositions(path: Path, width: number, height: number): Array<{ x: number; y: number; angle: number }> {
  if (path.points.length < 2) return [];
  
  const arrowSpacing = (path.arrowSpacing || 15) * (width / 100);
  const totalLength = getPathLength(path, width, height);
  const numArrows = Math.max(1, Math.floor(totalLength / arrowSpacing));
  const actualSpacing = totalLength / (numArrows + 1);
  
  const arrows: Array<{ x: number; y: number; angle: number }> = [];
  let currentDistance = actualSpacing;
  let accumulatedLength = 0;
  
  for (let i = 0; i < path.points.length - 1 && arrows.length < numArrows; i++) {
    const segmentLength = getSegmentLength(path, i, width, height);
    
    while (currentDistance <= accumulatedLength + segmentLength && arrows.length < numArrows) {
      const distanceIntoSegment = currentDistance - accumulatedLength;
      const t = distanceIntoSegment / segmentLength;
      
      const arrowPos = getPointOnPath(path, i, t, width, height);
      arrows.push(arrowPos);
      currentDistance += actualSpacing;
    }
    
    accumulatedLength += segmentLength;
  }
  
  return arrows;
}

// Calculate total path length
function getPathLength(path: Path, width: number, height: number): number {
  if (path.points.length < 2) return 0;
  
  let totalLength = 0;
  for (let i = 0; i < path.points.length - 1; i++) {
    totalLength += getSegmentLength(path, i, width, height);
  }
  
  return totalLength;
}

// Calculate segment length (approximated for smooth curves)
function getSegmentLength(path: Path, segmentIndex: number, width: number, height: number): number {
  if (segmentIndex >= path.points.length - 1) return 0;
  
  const p1 = path.points[segmentIndex];
  const p2 = path.points[segmentIndex + 1];
  
  if (!path.smooth) {
    // Straight line distance
    const dx = (p2.x - p1.x) / 100 * width;
    const dy = (p2.y - p1.y) / 100 * height;
    return Math.sqrt(dx * dx + dy * dy);
  } else {
    // Approximate smooth curve length by sampling
    let length = 0;
    const samples = 10;
    let prevPoint = getPointOnPath(path, segmentIndex, 0, width, height);
    
    for (let i = 1; i <= samples; i++) {
      const t = i / samples;
      const point = getPointOnPath(path, segmentIndex, t, width, height);
      const dx = point.x - prevPoint.x;
      const dy = point.y - prevPoint.y;
      length += Math.sqrt(dx * dx + dy * dy);
      prevPoint = point;
    }
    
    return length;
  }
}

// Get point on path at parameter t (0 to 1) along segment
function getPointOnPath(path: Path, segmentIndex: number, t: number, width: number, height: number): { x: number; y: number; angle: number } {
  if (segmentIndex >= path.points.length - 1) {
    const p = path.points[path.points.length - 1];
    return { 
      x: (p.x / 100) * width, 
      y: (p.y / 100) * height, 
      angle: 0 
    };
  }

  const i = segmentIndex;
  const p1 = path.points[i];
  const p2 = path.points[i + 1];

  if (!path.smooth) {
    // Linear interpolation
    const x = ((p1.x + (p2.x - p1.x) * t) / 100) * width;
    const y = ((p1.y + (p2.y - p1.y) * t) / 100) * height;
    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    return { x, y, angle };
  } else {
    // Smooth curve interpolation
    const p0 = i > 0 ? path.points[i - 1] : p1;
    const p3 = i + 2 < path.points.length ? path.points[i + 2] : p2;
    const tension = path.tension || 0.5;
    const alpha = tension;
    
    // Calculate control points for cubic Bézier curve
    const cp1x = p1.x + (p2.x - p0.x) / 6 * alpha;
    const cp1y = p1.y + (p2.y - p0.y) / 6 * alpha;
    const cp2x = p2.x - (p3.x - p1.x) / 6 * alpha;
    const cp2y = p2.y - (p3.y - p1.y) / 6 * alpha;

    // Cubic Bézier point calculation
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    const t2 = t * t;
    const t3 = t2 * t;

    const x = (mt3 * p1.x + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * p2.x) / 100 * width;
    const y = (mt3 * p1.y + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * p2.y) / 100 * height;

    // Tangent vector (derivative of cubic Bézier)
    const dx = 3 * mt2 * (cp1x - p1.x) + 6 * mt * t * (cp2x - cp1x) + 3 * t2 * (p2.x - cp2x);
    const dy = 3 * mt2 * (cp1y - p1.y) + 6 * mt * t * (cp2y - cp1y) + 3 * t2 * (p2.y - cp2y);

    const angle = Math.atan2(dy, dx);

    return { x, y, angle };
  }
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  size: number,
  color: string,
  style: 'triangle' | 'arrow' | 'chevron'
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);

  if (style === 'chevron') {
    ctx.strokeStyle = color;
    ctx.lineWidth = size * 0.15;
    ctx.beginPath();
    ctx.moveTo(-size, -size * 0.8);
    ctx.lineTo(0, 0);
    ctx.lineTo(-size, size * 0.8);
    ctx.stroke();
  } else if (style === 'arrow') {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(-size * 1.2, -size * 0.5);
    ctx.lineTo(size * 0.8, 0);
    ctx.lineTo(-size * 1.2, size * 0.5);
    ctx.lineTo(-size * 0.5, 0);
    ctx.closePath();
    ctx.fill();
  } else {
    // triangle
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(-size, -size * 0.7);
    ctx.lineTo(size, 0);
    ctx.lineTo(-size, size * 0.7);
    ctx.closePath();
    ctx.fill();
  }

  ctx.restore();
}

function drawElementOnCanvas(ctx: CanvasRenderingContext2D, element: SiteElement, width: number, height: number) {
  const x = (element.position.x / 100) * width;
  const y = (element.position.y / 100) * height;
  const color = element.color || getElementColorForCanvas(element.type);
  
  // Scale element size based on canvas width for consistent sizing
  const baseSize = element.size || 40;
  const scaleFactor = width / 1920; // Reference width
  const size = baseSize * scaleFactor * 2; // Scale up for better visibility in PDF

  ctx.save();

  if (element.type === 'text') {
    // Draw text element
    const baseFontSize = element.fontSize || 16;
    const fontSize = baseFontSize * scaleFactor * 1.5; // Scale up for PDF
    ctx.font = `${fontSize}px Arial`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(element.label, x, y);
  } else {
    // Draw marker with circle background (same as editor)
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, size / 2, 0, Math.PI * 2);
    ctx.fill();

    // Draw white border
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 3 * scaleFactor;
    ctx.stroke();

    // Draw icon/symbol in white on the marker
    ctx.fillStyle = 'white';
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2 * scaleFactor;
    drawElementIcon(ctx, element.type, x, y, size);
  }

  ctx.restore();
}

function drawElementIcon(ctx: CanvasRenderingContext2D, type: SiteElement['type'], x: number, y: number, size: number) {
  const iconSize = size * 0.5;
  
  ctx.save();
  ctx.translate(x, y);
  
  switch (type) {
    case 'start':
      // Flag icon
      ctx.beginPath();
      ctx.moveTo(-iconSize * 0.3, -iconSize * 0.4);
      ctx.lineTo(-iconSize * 0.3, iconSize * 0.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(-iconSize * 0.3, -iconSize * 0.4);
      ctx.lineTo(iconSize * 0.3, -iconSize * 0.2);
      ctx.lineTo(-iconSize * 0.3, 0);
      ctx.fill();
      break;
    case 'finish':
      // Checkered flag (damier)
      const squareSize = iconSize * 0.15;
      // Draw checkered pattern (4x4 grid)
      for (let row = 0; row < 4; row++) {
        for (let col = 0; col < 4; col++) {
          if ((row + col) % 2 === 0) {
            ctx.fillStyle = 'white';
          } else {
            ctx.fillStyle = 'white';
          }
          ctx.fillRect(
            -iconSize * 0.3 + col * squareSize,
            -iconSize * 0.3 + row * squareSize,
            squareSize,
            squareSize
          );
        }
      }
      break;
    case 'feed-zone':
      // Coffee cup
      ctx.beginPath();
      ctx.arc(0, 0, iconSize * 0.3, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(iconSize * 0.35, 0, iconSize * 0.15, -Math.PI / 2, Math.PI / 2);
      ctx.stroke();
      break;
    case 'wax-zone':
      // Waxing iron (fer à farter)
      // Iron plate (triangle shape)
      ctx.beginPath();
      ctx.moveTo(-iconSize * 0.35, iconSize * 0.3);
      ctx.lineTo(iconSize * 0.35, iconSize * 0.3);
      ctx.lineTo(0, -iconSize * 0.1);
      ctx.closePath();
      ctx.fill();
      // Handle
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, -iconSize * 0.1);
      ctx.lineTo(0, -iconSize * 0.4);
      ctx.stroke();
      break;
    case 'timing':
      // Clock
      ctx.beginPath();
      ctx.arc(0, 0, iconSize * 0.35, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -iconSize * 0.25);
      ctx.moveTo(0, 0);
      ctx.lineTo(iconSize * 0.2, 0);
      ctx.stroke();
      break;
    case 'exchange':
      // Repeat/exchange arrows
      ctx.beginPath();
      ctx.arc(0, -iconSize * 0.15, iconSize * 0.2, Math.PI, 0);
      ctx.moveTo(iconSize * 0.15, -iconSize * 0.15);
      ctx.lineTo(iconSize * 0.25, -iconSize * 0.25);
      ctx.moveTo(iconSize * 0.15, -iconSize * 0.15);
      ctx.lineTo(iconSize * 0.25, -iconSize * 0.05);
      ctx.stroke();
      break;
    case 'medical':
      // Cross
      ctx.fillRect(-iconSize * 0.15, -iconSize * 0.4, iconSize * 0.3, iconSize * 0.8);
      ctx.fillRect(-iconSize * 0.4, -iconSize * 0.15, iconSize * 0.8, iconSize * 0.3);
      break;
    case 'spectator':
      // Users/people
      ctx.beginPath();
      ctx.arc(-iconSize * 0.15, -iconSize * 0.15, iconSize * 0.15, 0, Math.PI * 2);
      ctx.arc(iconSize * 0.15, -iconSize * 0.15, iconSize * 0.15, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'loop-marker':
      // Circle with arrow returning to top (loop symbol)
      ctx.beginPath();
      ctx.arc(0, 0, iconSize * 0.3, 0, Math.PI * 1.75);
      ctx.stroke();
      // Arrow head at top
      ctx.beginPath();
      ctx.moveTo(iconSize * 0.21, -iconSize * 0.21);
      ctx.lineTo(iconSize * 0.35, -iconSize * 0.1);
      ctx.moveTo(iconSize * 0.21, -iconSize * 0.21);
      ctx.lineTo(iconSize * 0.1, -iconSize * 0.35);
      ctx.stroke();
      break;
    case 'camera':
      // Video camera
      ctx.fillRect(-iconSize * 0.3, -iconSize * 0.2, iconSize * 0.4, iconSize * 0.4);
      ctx.beginPath();
      ctx.moveTo(iconSize * 0.1, -iconSize * 0.1);
      ctx.lineTo(iconSize * 0.3, -iconSize * 0.2);
      ctx.lineTo(iconSize * 0.3, iconSize * 0.2);
      ctx.lineTo(iconSize * 0.1, iconSize * 0.1);
      ctx.fill();
      break;
    case 'rifle-control':
      // Rifle/carabine (vertical)
      ctx.beginPath();
      // Stock/crosse
      ctx.fillRect(-iconSize * 0.05, -iconSize * 0.45, iconSize * 0.1, iconSize * 0.15);
      // Barrel/canon
      ctx.fillRect(-iconSize * 0.03, -iconSize * 0.3, iconSize * 0.06, iconSize * 0.6);
      // Trigger guard/garde de détente
      ctx.arc(0, 0, iconSize * 0.08, Math.PI, 0, false);
      ctx.stroke();
      break;
    case 'parking':
      // P letter
      ctx.font = `${iconSize * 1.2}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('P', 0, 0);
      break;
    case 'locker-room':
      // Door
      ctx.strokeRect(-iconSize * 0.3, -iconSize * 0.4, iconSize * 0.6, iconSize * 0.8);
      ctx.beginPath();
      ctx.arc(iconSize * 0.15, 0, iconSize * 0.05, 0, Math.PI * 2);
      ctx.fill();
      break;
    case 'warmup-zone':
      // Cross-country skier symbol (side profile)
      // Head
      ctx.beginPath();
      ctx.arc(-iconSize * 0.25, -iconSize * 0.25, iconSize * 0.12, 0, Math.PI * 2);
      ctx.fill();
      // Body (leaning forward)
      ctx.beginPath();
      ctx.moveTo(-iconSize * 0.25, -iconSize * 0.13);
      ctx.lineTo(iconSize * 0.1, 0.05);
      // Back arm with pole (extended back)
      ctx.moveTo(-iconSize * 0.2, -iconSize * 0.05);
      ctx.lineTo(-iconSize * 0.45, iconSize * 0.35);
      // Front arm with pole (pushing forward)
      ctx.moveTo(-iconSize * 0.15, -iconSize * 0.05);
      ctx.lineTo(iconSize * 0.25, iconSize * 0.25);
      // Back leg (extended)
      ctx.moveTo(iconSize * 0.05, 0.05);
      ctx.lineTo(-iconSize * 0.15, iconSize * 0.4);
      // Front leg (bent, pushing)
      ctx.moveTo(iconSize * 0.1, 0.05);
      ctx.lineTo(iconSize * 0.35, iconSize * 0.35);
      ctx.stroke();
      break;
    case 'tribune':
      // Bleachers/gradins (stairs going up)
      ctx.beginPath();
      // Bottom step
      ctx.moveTo(-iconSize * 0.4, iconSize * 0.4);
      ctx.lineTo(-iconSize * 0.2, iconSize * 0.4);
      ctx.lineTo(-iconSize * 0.2, iconSize * 0.2);
      // Middle step
      ctx.lineTo(0, iconSize * 0.2);
      ctx.lineTo(0, 0);
      // Top step
      ctx.lineTo(iconSize * 0.2, 0);
      ctx.lineTo(iconSize * 0.2, -iconSize * 0.2);
      ctx.lineTo(iconSize * 0.4, -iconSize * 0.2);
      ctx.stroke();
      break;
    case 'danger-zone':
      // Warning triangle
      ctx.beginPath();
      ctx.moveTo(0, -iconSize * 0.4);
      ctx.lineTo(iconSize * 0.35, iconSize * 0.3);
      ctx.lineTo(-iconSize * 0.35, iconSize * 0.3);
      ctx.closePath();
      ctx.stroke();
      ctx.fillStyle = 'white';
      ctx.font = `bold ${iconSize * 1.2}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('!', 0, 0);
      break;
    case 'wc':
      // WC/Toilet symbol
      ctx.font = `bold ${iconSize * 1.2}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('WC', 0, 0);
      break;
    case 'ski-drop':
      // Pair of skis (parallel)
      ctx.lineWidth = 3;
      ctx.beginPath();
      // Left ski
      ctx.moveTo(-iconSize * 0.2, -iconSize * 0.4);
      ctx.lineTo(-iconSize * 0.2, iconSize * 0.4);
      // Right ski
      ctx.moveTo(iconSize * 0.2, -iconSize * 0.4);
      ctx.lineTo(iconSize * 0.2, iconSize * 0.4);
      ctx.stroke();
      // Ski tips
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-iconSize * 0.2, -iconSize * 0.4);
      ctx.lineTo(-iconSize * 0.15, -iconSize * 0.5);
      ctx.moveTo(iconSize * 0.2, -iconSize * 0.4);
      ctx.lineTo(iconSize * 0.25, -iconSize * 0.5);
      ctx.stroke();
      break;
    case 'secretariat':
      // House/building (same as old tribune)
      ctx.beginPath();
      // Roof
      ctx.moveTo(0, -iconSize * 0.4);
      ctx.lineTo(-iconSize * 0.35, -iconSize * 0.05);
      ctx.lineTo(iconSize * 0.35, -iconSize * 0.05);
      ctx.closePath();
      ctx.stroke();
      // House body
      ctx.strokeRect(-iconSize * 0.3, -iconSize * 0.05, iconSize * 0.6, iconSize * 0.5);
      // Door
      ctx.strokeRect(-iconSize * 0.1, iconSize * 0.15, iconSize * 0.2, iconSize * 0.3);
      break;
    case 'speaker':
      // Microphone
      ctx.beginPath();
      // Mic head
      ctx.arc(0, -iconSize * 0.25, iconSize * 0.12, 0, Math.PI * 2);
      // Mic body
      ctx.moveTo(0, -iconSize * 0.13);
      ctx.lineTo(0, iconSize * 0.2);
      // Stand base
      ctx.moveTo(-iconSize * 0.2, iconSize * 0.2);
      ctx.lineTo(iconSize * 0.2, iconSize * 0.2);
      ctx.stroke();
      break;
    default:
      // Default marker (simple circle)
      ctx.beginPath();
      ctx.arc(0, 0, iconSize * 0.25, 0, Math.PI * 2);
      ctx.fill();
      break;
  }
  
  ctx.restore();
}

function getDefaultPathColor(type: Path['type']): string {
  const colors = {
    course: '#3b82f6', // Bleu
    net: '#ef4444', // Rouge
    barrier: '#8b4513', // Marron (SaddleBrown)
    'v-board': '#ef4444', // Rouge
    sapinettes: '#ef4444', // Rouge
    custom: '#8b5cf6' // Violet
  };
  return colors[type] || '#3b82f6';
}

function getElementColorForCanvas(type: SiteElement['type']): string {
  const colors: { [key: string]: string } = {
    start: '#22c55e',
    finish: '#ef4444',
    'feed-zone': '#3b82f6',
    'wax-zone': '#a855f7',
    timing: '#eab308',
    exchange: '#14b8a6',
    medical: '#dc2626',
    spectator: '#6366f1',
    'loop-marker': '#f97316',
    camera: '#06b6d4',
    'rifle-control': '#f59e0b',
    parking: '#64748b',
    'locker-room': '#10b981',
    'warmup-zone': '#84cc16',
    tribune: '#f43f5e',
    wc: '#0ea5e9',
    'ski-drop': '#8b5cf6',
    'danger-zone': '#b91c1c',
    secretariat: '#3b82f6',
    speaker: '#a855f7',
    custom: '#8b5cf6',
    text: '#000000'
  };
  return colors[type] || '#8b5cf6';
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

async function cropPngDataUrl(dataUrl: string, x: number, y: number, w: number, h: number): Promise<string> {
  const img = await loadImage(dataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
  return canvas.toDataURL('image/png');
}
